import { useCallback, useEffect, useMemo, useState } from 'react';
import type React from 'react';
import type { AxiosError } from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Checkbox, FormControlLabel } from '@mui/material';
import { Save, Send, TestTube2 } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { AudienceSection } from '../components/AudienceSection';
import { ContentSection } from '../components/ContentSection';
import { NotificationPreview } from '../components/NotificationPreview';
import { SendConfirmDialog } from '../components/SendConfirmDialog';
import { TestEmailDialog } from '../components/TestEmailDialog';
import {
  useAudiencePreview,
  useCreateNotificationCampaign,
  useNotificationCampaign,
  useResolveNotificationEmails,
  useSendNotificationTest,
  useUpdateNotificationCampaign,
} from '../hooks/useNotificationCampaigns';
import { useUserFilterState } from '../../users';
import type {
  AdminNotificationApiError,
  AdminNotificationUser,
  AudiencePreviewRequest,
  CampaignChannel,
  CampaignAudienceType,
  CampaignDto,
  CampaignInput,
  LocalizedContent,
  LocalizedLabel,
  NotificationCategory,
  NotificationLocale,
  NotificationSeverity,
} from '../types';

const emptyContent: LocalizedContent = {
  vi: { title: '', body: '' },
  en: { title: '', body: '' },
};

const toLocalDateTime = (value: string | null) => (value ? value.slice(0, 16) : '');
const toIsoDateTime = (value: string) => (value ? new Date(value).toISOString() : null);
const apiErrorMessage = (error: unknown) =>
  (error as AxiosError<AdminNotificationApiError>).response?.data?.message ||
  'Unable to save notification.';

const useDebouncedValue = <T,>(value: T, delay: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);
  return debounced;
};

export const NotificationComposerPage: React.FC = () => {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const segmentState = useUserFilterState({ syncToUrl: false });
  const createMutation = useCreateNotificationCampaign();
  const updateMutation = useUpdateNotificationCampaign();
  const testMutation = useSendNotificationTest();
  const resolveEmailsMutation = useResolveNotificationEmails();
  const { data: loadedCampaign, isLoading } = useNotificationCampaign(publicId);

  const [savedCampaign, setSavedCampaign] = useState<CampaignDto | null>(null);
  const [audienceType, setAudienceType] = useState<CampaignAudienceType>('USERS');
  const [selectedUsers, setSelectedUsers] = useState<AdminNotificationUser[]>([]);
  const [notFoundEmails, setNotFoundEmails] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [channels, setChannels] = useState<CampaignChannel[]>(['IN_APP']);
  const [category, setCategory] = useState<NotificationCategory>('PRODUCT_UPDATE');
  const [severity, setSeverity] = useState<NotificationSeverity>('INFO');
  const [defaultLocale, setDefaultLocale] = useState<NotificationLocale>('vi');
  const [activeLocale, setActiveLocale] = useState<NotificationLocale>('vi');
  const [previewLocale, setPreviewLocale] = useState<NotificationLocale>('vi');
  const [content, setContent] = useState<LocalizedContent>(emptyContent);
  const [emailSubject, setEmailSubject] = useState<LocalizedLabel>({});
  const [ctaLabel, setCtaLabel] = useState<LocalizedLabel>({});
  const [ctaUrl, setCtaUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);

  useEffect(() => {
    if (!loadedCampaign) return;
    if (loadedCampaign.status !== 'DRAFT') {
      navigate(`/admin-mail/${loadedCampaign.publicId}`, { replace: true });
      return;
    }
    setSavedCampaign(loadedCampaign);
    setAudienceType(loadedCampaign.audienceType);
    setSelectedUsers(loadedCampaign.audienceUsers);
    if (loadedCampaign.audienceFilter) segmentState.setFilter(loadedCampaign.audienceFilter);
    setName(loadedCampaign.name);
    setChannels(loadedCampaign.channels.length > 0 ? loadedCampaign.channels : ['IN_APP']);
    setCategory(loadedCampaign.category);
    setSeverity(loadedCampaign.severity);
    setDefaultLocale(loadedCampaign.defaultLocale);
    setActiveLocale(loadedCampaign.defaultLocale);
    setPreviewLocale(loadedCampaign.defaultLocale);
    setContent({ ...emptyContent, ...loadedCampaign.content });
    setEmailSubject(loadedCampaign.emailSubject || {});
    setCtaLabel(loadedCampaign.ctaLabel || {});
    setCtaUrl(loadedCampaign.ctaUrl || '');
    setExpiresAt(toLocalDateTime(loadedCampaign.expiresAt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedCampaign, navigate]);

  const previewRequest = useMemo<AudiencePreviewRequest>(
    () => ({
      audienceType,
      filter: audienceType === 'SEGMENT' ? segmentState.filter : undefined,
      userPublicIds:
        audienceType === 'USERS' ? selectedUsers.map((user) => user.publicId) : undefined,
      channels,
      category,
    }),
    [audienceType, category, channels, segmentState.filter, selectedUsers]
  );
  const debouncedPreviewRequest = useDebouncedValue(previewRequest, 500);
  const { data: preview, isFetching: previewLoading } = useAudiencePreview(
    debouncedPreviewRequest,
    audienceType !== 'USERS' || selectedUsers.length > 0
  );

  const buildInput = useCallback((): CampaignInput => {
    const labels = Object.fromEntries(
      Object.entries(ctaLabel).filter(([, value]) => value.trim())
    ) as LocalizedLabel;
    const subjects = Object.fromEntries(
      Object.entries(emailSubject).filter(([, value]) => value.trim())
    ) as LocalizedLabel;
    return {
      name: name.trim(),
      channels,
      audienceType,
      audienceFilter: audienceType === 'SEGMENT' ? segmentState.filter : undefined,
      userPublicIds:
        audienceType === 'USERS'
          ? Array.from(new Set(selectedUsers.map((user) => user.publicId))).slice(0, 500)
          : undefined,
      category,
      severity,
      defaultLocale,
      content,
      emailSubject: Object.keys(subjects).length > 0 ? subjects : null,
      ctaLabel: Object.keys(labels).length > 0 ? labels : null,
      ctaUrl: ctaUrl.trim() || null,
      expiresAt: toIsoDateTime(expiresAt),
    };
  }, [
    audienceType,
    category,
    channels,
    content,
    ctaLabel,
    ctaUrl,
    defaultLocale,
    emailSubject,
    expiresAt,
    name,
    segmentState.filter,
    selectedUsers,
    severity,
  ]);

  const currentInput = useMemo(() => buildInput(), [buildInput]);
  const savedInput = useMemo<Partial<CampaignInput> | null>(() => {
    if (!savedCampaign) return null;
    return {
      name: savedCampaign.name,
      channels: savedCampaign.channels,
      audienceType: savedCampaign.audienceType,
      audienceFilter: savedCampaign.audienceFilter || undefined,
      userPublicIds:
        savedCampaign.audienceType === 'USERS'
          ? savedCampaign.audienceUsers.map((user) => user.publicId)
          : undefined,
      category: savedCampaign.category,
      severity: savedCampaign.severity,
      defaultLocale: savedCampaign.defaultLocale,
      content: { ...emptyContent, ...savedCampaign.content },
      emailSubject: savedCampaign.emailSubject,
      ctaLabel: savedCampaign.ctaLabel,
      ctaUrl: savedCampaign.ctaUrl,
      expiresAt: savedCampaign.expiresAt,
    };
  }, [savedCampaign]);
  const hasUnsavedChanges =
    !savedInput || JSON.stringify(currentInput) !== JSON.stringify(savedInput);
  const emailEnabled = channels.includes('EMAIL');

  const validate = () => {
    const localized = content[defaultLocale];
    const labels = Object.values(ctaLabel).filter((value) => value.trim());
    const subjects = Object.values(emailSubject).filter((value) => value.trim());
    if (!name.trim()) return 'Internal name is required.';
    if (name.length > 120) return 'Internal name must be 120 characters or less.';
    if (channels.length === 0) return 'Select at least one channel.';
    if (!localized?.title.trim() || !localized.body.trim()) {
      return 'Default locale title and body are required.';
    }
    if (subjects.some((subject) => subject.length > 150)) {
      return 'Email subject must be 150 characters or less.';
    }
    if (audienceType === 'USERS' && selectedUsers.length === 0) {
      return 'Select at least one user.';
    }
    if (labels.length > 0 && !ctaUrl.trim()) return 'CTA URL is required when CTA label is set.';
    if (ctaUrl.trim() && labels.length === 0) return 'CTA label is required when CTA URL is set.';
    if (ctaUrl.trim() && !ctaUrl.startsWith('/') && !ctaUrl.startsWith('https://')) {
      return 'CTA URL must start with / or https://.';
    }
    if (expiresAt && new Date(expiresAt).getTime() <= Date.now()) {
      return 'Expires at must be in the future.';
    }
    return '';
  };

  const saveDraft = async () => {
    const error = validate();
    if (error) {
      enqueueSnackbar(error, { variant: 'error' });
      return null;
    }
    try {
      const input = buildInput();
      const campaignPublicId = savedCampaign?.publicId || publicId;
      const campaign = campaignPublicId
        ? await updateMutation.mutateAsync({ publicId: campaignPublicId, data: input })
        : await createMutation.mutateAsync(input);
      setSavedCampaign(campaign);
      enqueueSnackbar('Draft saved.', { variant: 'success' });
      if (!publicId) navigate(`/admin-mail/${campaign.publicId}/edit`, { replace: true });
      return campaign;
    } catch (error) {
      const message = apiErrorMessage(error);
      enqueueSnackbar(message, { variant: 'error' });
      return null;
    }
  };

  const handleResolveEmails = async (emails: string[]) => {
    const result = await resolveEmailsMutation.mutateAsync(emails);
    setNotFoundEmails(result.notFound);
    setSelectedUsers((current) => {
      const merged = new Map(current.map((user) => [user.publicId, user]));
      result.found.forEach((user) => merged.set(user.publicId, user));
      return Array.from(merged.values()).slice(0, 500);
    });
    enqueueSnackbar(`${result.found.length} users added.`, { variant: 'success' });
  };

  const handleTest = async () => {
    if (!savedCampaign?.publicId) {
      enqueueSnackbar('Save the draft before sending a test.', { variant: 'warning' });
      return;
    }
    if (emailEnabled) {
      setTestDialogOpen(true);
      return;
    }
    await sendTest();
  };

  const sendTest = async (emails?: string[]) => {
    if (!savedCampaign?.publicId) return;
    try {
      const result = await testMutation.mutateAsync({ publicId: savedCampaign.publicId, emails });
      setTestDialogOpen(false);
      const suffix =
        result.emailsSent.length > 0 ? ` Email sent to ${result.emailsSent.join(', ')}.` : '';
      enqueueSnackbar(`Test notification sent.${suffix}`, { variant: 'success' });
    } catch (error) {
      const response = (error as AxiosError<AdminNotificationApiError>).response?.data;
      enqueueSnackbar(
        response?.error === 'TEST_RATE_LIMITED'
          ? 'Test limit reached. Try again later.'
          : response?.message || 'Unable to send test.',
        { variant: 'error' }
      );
    }
  };

  const toggleChannel = (channel: CampaignChannel) => {
    setChannels((current) => {
      if (current.includes(channel)) {
        return current.length > 1 ? current.filter((item) => item !== channel) : current;
      }
      return [...current, channel];
    });
  };

  const handleReview = async () => {
    const campaign = await saveDraft();
    if (campaign) setConfirmOpen(true);
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  if (publicId && isLoading) {
    return <div className="p-6 text-gray-900 dark:text-white">Loading notification...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 dark:bg-slate-900 md:p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {publicId ? 'Edit notification' : 'New notification'}
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">
          Create an in-app campaign, preview it, then confirm the final audience.
        </p>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5">
          <AudienceSection
            audienceType={audienceType}
            selectedUsers={selectedUsers}
            segmentFilter={segmentState.filter}
            preview={preview}
            previewLoading={previewLoading}
            emailEnabled={emailEnabled}
            notFoundEmails={notFoundEmails}
            resolvingEmails={resolveEmailsMutation.isPending}
            onAudienceTypeChange={setAudienceType}
            onSelectedUsersChange={setSelectedUsers}
            onSegmentFilterChange={segmentState.setFilter}
            onResolveEmails={handleResolveEmails}
          />

          <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">Channels</h2>
            <div className="flex flex-wrap items-center gap-4">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={channels.includes('IN_APP')}
                    onChange={() => toggleChannel('IN_APP')}
                  />
                }
                label="In-app (bell)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={channels.includes('EMAIL')}
                    onChange={() => toggleChannel('EMAIL')}
                  />
                }
                label="Email"
              />
            </div>
          </section>

          <ContentSection
            name={name}
            category={category}
            severity={severity}
            defaultLocale={defaultLocale}
            activeLocale={activeLocale}
            content={content}
            emailEnabled={emailEnabled}
            emailSubject={emailSubject}
            ctaLabel={ctaLabel}
            ctaUrl={ctaUrl}
            expiresAt={expiresAt}
            onNameChange={setName}
            onCategoryChange={setCategory}
            onSeverityChange={setSeverity}
            onDefaultLocaleChange={setDefaultLocale}
            onActiveLocaleChange={setActiveLocale}
            onContentChange={(locale, value) =>
              setContent((current) => ({
                ...current,
                [locale]: {
                  title: current[locale]?.title || '',
                  body: current[locale]?.body || '',
                  ...value,
                },
              }))
            }
            onEmailSubjectChange={(locale, value) =>
              setEmailSubject((current) => ({ ...current, [locale]: value }))
            }
            onCtaLabelChange={(locale, value) =>
              setCtaLabel((current) => ({ ...current, [locale]: value }))
            }
            onCtaUrlChange={setCtaUrl}
            onExpiresAtChange={setExpiresAt}
          />

          <section className="flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800">
            <Button
              variant="outlined"
              startIcon={<Save size={18} />}
              disabled={saving}
              onClick={() => void saveDraft()}
            >
              Save draft
            </Button>
            <Button
              variant="outlined"
              startIcon={<TestTube2 size={18} />}
              disabled={testMutation.isPending}
              onClick={() => void handleTest()}
            >
              Send test to me
            </Button>
            <Button
              variant="contained"
              startIcon={<Send size={18} />}
              disabled={saving}
              onClick={() => void handleReview()}
            >
              Review & send
            </Button>
          </section>
        </div>

        <aside className="xl:sticky xl:top-6 xl:self-start">
          <NotificationPreview
            category={category}
            severity={severity}
            content={content}
            ctaLabel={Object.keys(ctaLabel).length > 0 ? ctaLabel : null}
            ctaUrl={ctaUrl || null}
            defaultLocale={defaultLocale}
            locale={previewLocale}
            channels={channels}
            emailPreviewPublicId={savedCampaign?.publicId}
            emailPreviewDirty={hasUnsavedChanges}
            onLocaleChange={setPreviewLocale}
          />
        </aside>
      </div>

      <SendConfirmDialog
        open={confirmOpen}
        campaign={savedCampaign}
        onClose={() => setConfirmOpen(false)}
        onSent={(campaign) => navigate(`/admin-mail/${campaign.publicId}`)}
      />
      <TestEmailDialog
        open={testDialogOpen}
        sending={testMutation.isPending}
        onClose={() => setTestDialogOpen(false)}
        onSend={(emails) => void sendTest(emails)}
      />
    </div>
  );
};
